// Framework-agnostic storage + derivation for the segment-lifecycle stubs.
// No React / Mantine imports (layer rule for features/*/api.ts). Browser globals
// (localStorage, window) are guarded so this is safe under node/vitest.

import { addWeeks, isBefore, parseISO, startOfWeek } from 'date-fns'
import { DatasetType } from '@/lib/domain'
import type { AudienceConfiguration } from '@/lib/domain'
import {
    defaultDestinationRoute,
    findDestination,
    isRouteConfigured,
} from '@/lib/marketplaceDestinations'
import type {
    ConfigSlotRef,
    ConfigValues,
    DestinationRoute,
    MarketplaceProvider,
    SavedConfig,
} from '@/lib/marketplaceDestinations'
import type {
    AudienceInsightChart,
    Destination,
    Distribution,
    DistributeStageStatus,
    DistributionConfigState,
    DistributionRun,
    DistributionSchedule,
    Modeling,
    ModelingPriority,
    ModelStageStatus,
    RunStatus,
    SegmentDelivery,
    SegmentKey,
    SegmentLifecycle,
    SegmentStubStore,
} from './types'

// v3: runs are no longer auto-created/stubbed; bumped to discard any leftover
// stub runs from earlier versions. Real run history is populated on demand (the
// hidden demo trigger) or, eventually, by the backend.
const STORAGE_KEY = 'abd.segment-stubs.v3'

export function emptyStore(): SegmentStubStore {
    return {
        modelings: [],
        distributions: [],
        runs: [],
        destinations: [],
        destinationConfig: { marketplace: {}, direct: {}, destination: {} },
    }
}

// Key for the per-destination setup slot (destination name + marketplace).
const destSetupKey = (name: string, via: MarketplaceProvider): string =>
    `${name}__${via}`

// Build the lib's SavedConfig accessors over the stored partial values.
export function savedConfigFromStore(store: SegmentStubStore): SavedConfig {
    const c = store.destinationConfig
    return {
        marketplace: (provider) => c.marketplace[provider],
        direct: (name) => c.direct[name],
        destination: (name, via) => c.destination[destSetupKey(name, via)],
    }
}

// Merge partial values into one config slot (never clobbers other fields).
export function setDestinationConfig(
    store: SegmentStubStore,
    ref: ConfigSlotRef,
    values: ConfigValues
): SegmentStubStore {
    const c = store.destinationConfig
    if (ref.kind === 'marketplace') {
        return {
            ...store,
            destinationConfig: {
                ...c,
                marketplace: {
                    ...c.marketplace,
                    [ref.provider]: {
                        ...c.marketplace[ref.provider],
                        ...values,
                    },
                },
            },
        }
    }
    if (ref.kind === 'direct') {
        return {
            ...store,
            destinationConfig: {
                ...c,
                direct: {
                    ...c.direct,
                    [ref.destination]: {
                        ...c.direct[ref.destination],
                        ...values,
                    },
                },
            },
        }
    }
    const key = destSetupKey(ref.destination, ref.via)
    return {
        ...store,
        destinationConfig: {
            ...c,
            destination: {
                ...c.destination,
                [key]: { ...c.destination[key], ...values },
            },
        },
    }
}

// localStorage primitives — the mock persistence layer behind SegmentService.
// When this moves to a real backend, only SegmentService's method bodies change
// (these are replaced by fetch calls); the async surface stays identical.
function readStoreSync(): SegmentStubStore {
    if (typeof localStorage === 'undefined') return emptyStore()
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return emptyStore()
        const parsed = JSON.parse(raw) as Partial<SegmentStubStore>
        return { ...emptyStore(), ...parsed }
    } catch {
        return emptyStore()
    }
}

function writeStoreSync(next: SegmentStubStore): void {
    if (typeof localStorage === 'undefined') return
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
}

// --- Audience insights cache (separate from the lifecycle store) ---
// The insights endpoints are LLM-backed and slow; each chart carries the DB query
// (`search_request`) that produced it, plus the bar data and the time it was
// fetched. The underlying data refreshes weekly (Monday), so within the same
// data-week we serve the cached bars with no network call; once a Monday
// boundary is crossed we re-run the searches live (refreshAudienceInsights),
// and only rebuild via the LLM when the segment's targeting changes (fingerprint
// mismatch). Kept in its own key — read-only backend output, not lifecycle edits.
const INSIGHTS_CACHE_KEY = 'abd.insights-cache.v1'

interface InsightsCacheEntry {
    // Hash of the targeting that produced these searches; a mismatch on read
    // means the segment was edited, so the entry is stale (→ rebuild via LLM).
    fingerprint: string
    charts: AudienceInsightChart[]
    // ISO time the `charts` data (bar numbers) was last produced — by the LLM
    // build or a live refresh; drives the Monday-boundary check. Optional for
    // backward-compat with pre-timestamp entries (treated as stale).
    fetchedAt?: string
    // ISO time the chart *layout* was generated by the LLM. Set on an LLM build
    // and preserved across refreshes (a refresh re-runs the same searches, only
    // the numbers change). Absent on pre-existing entries until the next build.
    generatedAt?: string
}

// Cache metadata recorded alongside the charts.
export interface InsightsMeta {
    fetchedAt: string
    generatedAt?: string
}

export interface CachedInsights extends InsightsMeta {
    charts: AudienceInsightChart[]
}

type InsightsCache = Record<SegmentKey, InsightsCacheEntry>

function readInsightsCacheSync(): InsightsCache {
    if (typeof localStorage === 'undefined') return {}
    try {
        const raw = localStorage.getItem(INSIGHTS_CACHE_KEY)
        return raw ? (JSON.parse(raw) as InsightsCache) : {}
    } catch {
        return {}
    }
}

function writeInsightsCacheSync(cache: InsightsCache): void {
    if (typeof localStorage === 'undefined') return
    localStorage.setItem(INSIGHTS_CACHE_KEY, JSON.stringify(cache))
}

// Stable fingerprint of the targeting that determines a segment's searches — a
// change means the segment was edited, so the cached layout is stale (rebuild via
// LLM). Each type hashes exactly the fields its endpoint searches on: transaction
// behaviors, attribute filters, or both (plus the attribute dataset id) for a
// cross.
export function insightsFingerprint(config: AudienceConfiguration): string {
    switch (config.dataset_type) {
        case DatasetType.TRANSACTION:
            return JSON.stringify(config.users_with_transactions ?? [])
        case DatasetType.USER_ATTRIBUTE:
            return JSON.stringify(config.users_with_attributes ?? {})
        case DatasetType.CROSSED:
            return JSON.stringify([
                config.users_with_transactions ?? [],
                config.users_with_attributes ?? {},
                config.users_with_attributes_dataset_id,
            ])
    }
}

// Local-time midnight of the most recent Monday at or before `nowMs` — the start
// of the current data-week (the weekly refresh boundary).
export function mostRecentMonday(nowMs: number): Date {
    return startOfWeek(new Date(nowMs), { weekStartsOn: 1 })
}

// The upcoming weekly refresh: next Monday 00:00 (this week's Monday + 7 days).
export function nextRefreshAt(nowMs: number): Date {
    return addWeeks(mostRecentMonday(nowMs), 1)
}

// Cached data is fresh if it was fetched on or after the current week's Monday
// boundary — i.e. no weekly refresh has happened since. Missing/invalid
// timestamps count as stale.
export function insightsCacheFresh(
    fetchedAt: string | undefined,
    nowMs: number
): boolean {
    if (!fetchedAt) return false
    const t = parseISO(fetchedAt)
    return !Number.isNaN(t.getTime()) && !isBefore(t, mostRecentMonday(nowMs))
}

// The cached entry for a segment, but only if the fingerprint still matches
// (else the segment was edited and the entry is stale). Returns the charts +
// timestamps so the caller can apply the Monday-boundary check and show
// data-run / layout-generated notes.
export function getCachedInsights(
    key: SegmentKey,
    fingerprint: string
): CachedInsights | null {
    const entry = readInsightsCacheSync()[key]
    if (!entry || entry.fingerprint !== fingerprint) return null
    return {
        charts: entry.charts,
        fetchedAt: entry.fetchedAt ?? '',
        generatedAt: entry.generatedAt,
    }
}

// Upsert (overwrites the segment's single entry — no orphan accumulation).
export function setCachedInsights(
    key: SegmentKey,
    fingerprint: string,
    charts: AudienceInsightChart[],
    meta: InsightsMeta
): void {
    const cache = readInsightsCacheSync()
    cache[key] = {
        fingerprint,
        charts,
        fetchedAt: meta.fetchedAt,
        generatedAt: meta.generatedAt,
    }
    writeInsightsCacheSync(cache)
}

export function clearCachedInsights(key: SegmentKey): void {
    const cache = readInsightsCacheSync()
    if (key in cache) {
        delete cache[key]
        writeInsightsCacheSync(cache)
    }
}

// Composite key — mirrors the cart render key in ThreadPage.
export function segmentKey(
    config: Pick<
        AudienceConfiguration,
        'dataset_type' | 'segment_id' | 'dataset_id'
    >
): SegmentKey {
    return `${config.dataset_type}-${config.segment_id}-${config.dataset_id}`
}

// Stable per-segment match estimate (38..86%). Deterministic hash of the key so
// the same segment shows the same estimate across renders and reloads, with no
// real source to back it (v1). Replaced by the actual rate once distributed.
export function estimateMatchPct(key: SegmentKey): number {
    let hash = 7
    for (let i = 0; i < key.length; i++) {
        hash = (hash * 31 + key.charCodeAt(i)) >>> 0
    }
    return 38 + (hash % 49)
}

// --- Modeling priority: one knob, everything derived from it ---
//
// The reach multiplier is the single source of truth for a priority. The model's
// "tightness" (0 = broadest reach, 1 = tightest similarity) is derived from it,
// and the post-run quality curves (similarity bands, propensity deciles) are in
// turn derived from tightness. Change a multiplier and the size, the similarity
// spread, and the decile steepness all move together — they can't drift apart.
//
// Mirrors AudienceExpansionPage so the lift number matches the existing page.
export const MODEL_MULTIPLIERS: Record<ModelingPriority, number> = {
    similarity: 0.7,
    balanced: 1.0,
    reach: 1.5,
}
const REACH_MULT_TIGHT = MODEL_MULTIPLIERS.similarity // smallest, most similar
const REACH_MULT_BROAD = MODEL_MULTIPLIERS.reach // largest, least similar

export function computeExpandedSize(
    seedSize: number,
    similarityPct: number,
    priority: ModelingPriority
): number {
    return Math.round(seedSize * similarityPct * MODEL_MULTIPLIERS[priority])
}

// 0 = broadest reach, 1 = tightest similarity. Derived from the reach multiplier
// so the multiplier stays the one source of truth.
export function modelTightness(priority: ModelingPriority): number {
    return (
        (REACH_MULT_BROAD - MODEL_MULTIPLIERS[priority]) /
        (REACH_MULT_BROAD - REACH_MULT_TIGHT)
    )
}

// Round a set of percentages so they still sum to exactly 100 (the leftover from
// rounding lands on the largest band).
function roundToHundred(values: number[]): number[] {
    const rounded = values.map((v) => Math.round(v))
    const diff = 100 - rounded.reduce((a, b) => a + b, 0)
    if (diff !== 0) {
        const largest = rounded.indexOf(Math.max(...rounded))
        rounded[largest] += diff
    }
    return rounded
}

// Similarity-band shares [90–100, 80–90, 70–80, 60–70], summing to 100. A tight
// model concentrates mass in the high-similarity bands; a broad one spreads it
// toward the loose bands. Linear ramp whose slope flips sign with tightness.
export function similarityShares(priority: ModelingPriority): number[] {
    const slope = 0.5 * (1 - 2 * modelTightness(priority))
    const raw = [0, 1, 2, 3].map((i) => 1 + slope * (i - 1.5))
    const total = raw.reduce((a, b) => a + b, 0)
    return roundToHundred(raw.map((w) => (w / total) * 100))
}

// Predicted conversion rate (%) per decile, 1 (most likely) → 10 (least). A
// tight model is more top-loaded (higher top decile, steeper decay); a broad one
// is flatter.
export function propensityDeciles(priority: ModelingPriority): number[] {
    const t = modelTightness(priority)
    const top = 28 + 18 * t
    const decay = 0.12 + 0.33 * t
    return Array.from({ length: 10 }, (_, i) =>
        Math.max(1, Math.round(top * Math.exp(-decay * i)))
    )
}

// --- Pure store transforms (return the next store; caller persists) ---

export function upsertModeling(
    store: SegmentStubStore,
    modeling: Modeling
): SegmentStubStore {
    const matches = (m: Modeling) =>
        m.threadId === modeling.threadId && m.segmentKey === modeling.segmentKey
    const exists = store.modelings.some(matches)
    return {
        ...store,
        modelings: exists
            ? store.modelings.map((m) => (matches(m) ? modeling : m))
            : [...store.modelings, modeling],
    }
}

// Upsert config Distributions by id (re-saving a segment×destination updates its
// config, e.g. schedule/route, without touching its run history).
export function upsertDistributions(
    store: SegmentStubStore,
    distributions: Distribution[]
): SegmentStubStore {
    const incoming = new Set(distributions.map((d) => d.id))
    return {
        ...store,
        distributions: [
            ...store.distributions.filter((d) => !incoming.has(d.id)),
            ...distributions,
        ],
    }
}

export function updateDistribution(
    store: SegmentStubStore,
    id: string,
    patch: Partial<Distribution>
): SegmentStubStore {
    return {
        ...store,
        distributions: store.distributions.map((d) =>
            d.id === id ? { ...d, ...patch } : d
        ),
    }
}

// Append runs (replacing any with the same id).
export function addRuns(
    store: SegmentStubStore,
    runs: DistributionRun[]
): SegmentStubStore {
    const incoming = new Set(runs.map((r) => r.id))
    return {
        ...store,
        runs: [...store.runs.filter((r) => !incoming.has(r.id)), ...runs],
    }
}

// Add a destination to the connectable list (no-op if already present).
export function addDestination(
    store: SegmentStubStore,
    destination: Destination
): SegmentStubStore {
    if (store.destinations.some((d) => d.id === destination.id)) return store
    return { ...store, destinations: [...store.destinations, destination] }
}

// Remove a destination and its connection config (direct connection +
// per-destination setup slots); shared marketplace accounts are kept. Existing
// distributions + runs are intentionally left in place — without their
// destination they surface as needs-config (a partially-completed delivery that
// keeps its run history), rather than disappearing.
export function removeDestination(
    store: SegmentStubStore,
    id: string
): SegmentStubStore {
    const platform = store.destinations.find((d) => d.id === id)?.platform
    const config = store.destinationConfig
    const direct = { ...config.direct }
    const destination = { ...config.destination }
    if (platform) {
        delete direct[platform]
        for (const key of Object.keys(destination)) {
            if (key.startsWith(`${platform}__`)) delete destination[key]
        }
    }
    return {
        ...store,
        destinations: store.destinations.filter((d) => d.id !== id),
        destinationConfig: { ...config, direct, destination },
    }
}

// Remove a distribution config and its run history.
export function removeDistribution(
    store: SegmentStubStore,
    id: string
): SegmentStubStore {
    return {
        ...store,
        distributions: store.distributions.filter((d) => d.id !== id),
        runs: store.runs.filter((r) => r.distributionId !== id),
    }
}

// --- Distribution construction (pure; `now` injected for determinism) ---

function distributionId(
    threadId: string,
    key: SegmentKey,
    destinationId: string
): string {
    return `${threadId}::${key}::${destinationId}`
}

// The route a destination resolves to at config time: a direct integration wins;
// otherwise its first carrying marketplace. Multi-marketplace destinations
// default to their first carrier (re-routable in the config modal) rather than
// starting routeless — so a delivery is never "needs-config" purely for an
// unmade route choice.
export function routeForPlatform(
    platform: string
): DestinationRoute | undefined {
    const dest = findDestination(platform)
    if (!dest) return undefined
    return defaultDestinationRoute(dest)
}

export interface BuildDistributionConfigParams {
    threadId: string
    segmentKey: SegmentKey
    destination: Destination
    schedule: DistributionSchedule
    now: string
}

// The CONFIG object for a segment × destination. Route is resolved from the
// marketplace model by platform name (undefined for legacy/unknown platforms).
export function buildDistributionConfig({
    threadId,
    segmentKey: key,
    destination,
    schedule,
    now,
}: BuildDistributionConfigParams): Distribution {
    return {
        id: distributionId(threadId, key, destination.id),
        threadId,
        segmentKey: key,
        destinationId: destination.id,
        route: routeForPlatform(destination.platform),
        schedule,
        createdAt: now,
    }
}

// --- Demo run history (hidden trigger only — never auto-invoked) ---

// Pure generator input. `count` (1-5) is chosen by the caller so this stays
// deterministic/testable; the service rolls the randomness.
export interface DemoRunParams {
    distributionId: string
    recordsBase: number
    matchPct: number
    dataDelivery: boolean
    count: number
    now: string
}

const DAY_MS = 24 * 3600 * 1000

// Fabricate a realistic run history: `count` recent runs spaced a week apart
// (newest = `now`), mostly succeeded with a partial once there are a few, records
// drifting slightly week over week. Data-delivery destinations have no match step
// (recordsMatched: null). Used only by the hidden demo affordance.
export function buildDemoRuns({
    distributionId,
    recordsBase,
    matchPct,
    dataDelivery,
    count,
    now,
}: DemoRunParams): DistributionRun[] {
    const n = Math.max(1, Math.min(5, count))
    return Array.from({ length: n }, (_, i): DistributionRun => {
        const daysAgo = i * 7
        const runAt = new Date(Date.parse(now) - daysAgo * DAY_MS).toISOString()
        const pushed = Math.max(
            0,
            Math.round(recordsBase * (1 - daysAgo * 0.015))
        )
        // Newest succeeds; the second is a partial for variety, but only once
        // there are enough runs to show a trend.
        const status: RunStatus = i === 1 && n > 2 ? 'partial' : 'succeeded'
        const rate = status === 'partial' ? matchPct * 0.6 : matchPct
        return {
            id: `${distributionId}::${runAt}`,
            distributionId,
            status,
            runAt,
            recordsPushed: pushed,
            recordsMatched: dataDelivery
                ? null
                : Math.round((pushed * rate) / 100),
        }
    })
}

// Args for the demo trigger (schedule-based; the service rolls a 1-5 run count).
export interface DemoRunHistoryArgs {
    distributionId: string
    recordsBase: number
    matchPct: number
    dataDelivery: boolean
    schedule: DistributionSchedule
}

// Input for saving a segment's deliveries from the Distribute tab.
export interface BuildDistributionsParams {
    threadId: string
    segmentKey: SegmentKey
    destinations: Destination[]
    schedule?: DistributionSchedule
    now: string
}

// --- Read-time derivations ---

export function modelingFor(
    store: SegmentStubStore,
    threadId: string,
    key: SegmentKey
): Modeling | undefined {
    return store.modelings.find(
        (m) => m.threadId === threadId && m.segmentKey === key
    )
}

export function distributionsFor(
    store: SegmentStubStore,
    threadId: string,
    key: SegmentKey
): Distribution[] {
    return store.distributions.filter(
        (d) => d.threadId === threadId && d.segmentKey === key
    )
}

// A distribution's runs, newest-first.
export function runsForDistribution(
    store: SegmentStubStore,
    distributionId: string
): DistributionRun[] {
    return store.runs
        .filter((r) => r.distributionId === distributionId)
        .sort(
            (a, b) =>
                (b.runAt ? Date.parse(b.runAt) : 0) -
                (a.runAt ? Date.parse(a.runAt) : 0)
        )
}

// Derived config state: needs-config (lazy setup incomplete) > paused > active.
// Platforms not on the marketplace model have no lazy-config concept.
export function deriveConfigState(
    store: SegmentStubStore,
    distribution: Distribution
): DistributionConfigState {
    const dest = store.destinations.find(
        (d) => d.id === distribution.destinationId
    )
    // Destination was deleted out from under this delivery — orphaned, can't run.
    if (!dest) return 'needs-config'
    const marketplaceDest = findDestination(dest.platform)
    if (marketplaceDest) {
        // An unsaved route defaults to the first carrier (see routeForPlatform),
        // so completeness turns purely on the connection/setup fields.
        const route =
            distribution.route ?? defaultDestinationRoute(marketplaceDest)
        if (
            !isRouteConfigured(
                marketplaceDest,
                route,
                savedConfigFromStore(store)
            )
        ) {
            return 'needs-config'
        }
    }
    return distribution.paused ? 'paused' : 'active'
}

// Configured deliveries for a segment, each with its run history + latest run.
export function deliveriesFor(
    store: SegmentStubStore,
    threadId: string,
    key: SegmentKey
): SegmentDelivery[] {
    return distributionsFor(store, threadId, key).map((distribution) => {
        const runs = runsForDistribution(store, distribution.id)
        return {
            distribution,
            latestRun: runs[0],
            runs,
            configState: deriveConfigState(store, distribution),
        }
    })
}

export function deriveModelStatus(modeling?: Modeling): ModelStageStatus {
    if (!modeling) return 'none'
    return modeling.status === 'ready' ? 'ready' : 'draft'
}

export function deriveDistributeStatus(
    deliveries: SegmentDelivery[]
): DistributeStageStatus {
    if (deliveries.length === 0) return 'none'
    if (
        deliveries.some(
            (d) =>
                d.latestRun?.status === 'failed' ||
                d.configState === 'needs-config'
        )
    ) {
        return 'attention'
    }
    if (
        deliveries.some(
            (d) =>
                d.latestRun?.status === 'succeeded' ||
                d.latestRun?.status === 'partial'
        )
    ) {
        return 'live'
    }
    return 'pending'
}

// Outstanding next steps: a run's own nextStep, plus a setup nudge for any
// delivery whose config is incomplete.
export function deriveNextSteps(
    store: SegmentStubStore,
    deliveries: SegmentDelivery[]
): string[] {
    const steps: string[] = []
    for (const d of deliveries) {
        if (d.configState === 'needs-config') {
            const platform =
                store.destinations.find(
                    (x) => x.id === d.distribution.destinationId
                )?.platform ?? d.distribution.destinationId
            steps.push(`Finish setup for ${platform}`)
        }
        if (d.latestRun?.nextStep) steps.push(d.latestRun.nextStep)
    }
    return steps
}

export function lifecycleFor(
    store: SegmentStubStore,
    threadId: string,
    key: SegmentKey
): SegmentLifecycle {
    const modeling = modelingFor(store, threadId, key)
    const deliveries = deliveriesFor(store, threadId, key)
    return {
        segmentKey: key,
        estimatedMatchPct: estimateMatchPct(key),
        modeling,
        modelStatus: deriveModelStatus(modeling),
        deliveries,
        distributeStatus: deriveDistributeStatus(deliveries),
        liveDestinationCount: deliveries.filter(
            (d) =>
                d.latestRun?.status === 'succeeded' ||
                d.latestRun?.status === 'partial'
        ).length,
        nextSteps: deriveNextSteps(store, deliveries),
    }
}

// --- Async service (mock, localStorage-backed) ---
//
// Every method is async with the same signature a real server client would
// expose, so the React layer (queries.ts) never changes when these bodies are
// swapped for fetch calls. A small artificial latency makes the async nature
// (and its loading/refetch behavior) realistic in the demo.

const MOCK_LATENCY_MS = 150

const delay = (ms: number): Promise<void> =>
    new Promise((resolve) => setTimeout(resolve, ms))

export class SegmentService {
    // GET the whole overlay. (A server split into per-resource endpoints would
    // change only this body; the derivations in api.ts stay client-side.)
    public async fetchStore(): Promise<SegmentStubStore> {
        await delay(MOCK_LATENCY_MS)
        return readStoreSync()
    }

    public async saveModeling(modeling: Modeling): Promise<void> {
        await delay(MOCK_LATENCY_MS)
        writeStoreSync(upsertModeling(readStoreSync(), modeling))
    }

    // Save this segment's deliveries as exactly the chosen set of config
    // Distributions, so the sheet mirrors current state (re-checking keeps,
    // unchecking removes). Runs are NOT created here — real run history would be
    // produced by actual delivery execution, not stubbed.
    public async setSegmentDistributions(
        args: Omit<BuildDistributionsParams, 'now'>
    ): Promise<void> {
        await delay(MOCK_LATENCY_MS)
        const now = new Date().toISOString()
        const store = readStoreSync()
        const schedule = args.schedule ?? 'weekly'

        const configs = args.destinations.map((destination) =>
            buildDistributionConfig({
                threadId: args.threadId,
                segmentKey: args.segmentKey,
                destination,
                schedule,
                now,
            })
        )
        const otherConfigs = store.distributions.filter(
            (d) =>
                !(
                    d.threadId === args.threadId &&
                    d.segmentKey === args.segmentKey
                )
        )
        writeStoreSync({
            ...store,
            distributions: [...otherConfigs, ...configs],
        })
    }

    // Hidden demo affordance: generate a realistic run history for a distribution
    // that has none yet. No-op if it already has runs. Rolls a random 1-5 run
    // count (one-time deliveries always get a single run).
    public async addDemoRunHistory(args: DemoRunHistoryArgs): Promise<void> {
        await delay(MOCK_LATENCY_MS)
        const store = readStoreSync()
        if (store.runs.some((r) => r.distributionId === args.distributionId)) {
            return
        }
        const count =
            args.schedule === 'once' ? 1 : 1 + Math.floor(Math.random() * 5)
        const runs = buildDemoRuns({
            distributionId: args.distributionId,
            recordsBase: args.recordsBase,
            matchPct: args.matchPct,
            dataDelivery: args.dataDelivery,
            count,
            now: new Date().toISOString(),
        })
        writeStoreSync(addRuns(store, runs))
    }

    public async setDistribution(
        id: string,
        patch: Partial<Distribution>
    ): Promise<void> {
        await delay(MOCK_LATENCY_MS)
        writeStoreSync(updateDistribution(readStoreSync(), id, patch))
    }

    public async removeDistribution(id: string): Promise<void> {
        await delay(MOCK_LATENCY_MS)
        writeStoreSync(removeDistribution(readStoreSync(), id))
    }

    public async addDestination(destination: Destination): Promise<void> {
        await delay(MOCK_LATENCY_MS)
        writeStoreSync(addDestination(readStoreSync(), destination))
    }

    public async removeDestination(id: string): Promise<void> {
        await delay(MOCK_LATENCY_MS)
        writeStoreSync(removeDestination(readStoreSync(), id))
    }

    // Persist partial config for one slot (marketplace account, direct
    // connection, or per-destination setup). Merges into whatever's saved.
    public async setDestinationConfig(
        ref: ConfigSlotRef,
        values: ConfigValues
    ): Promise<void> {
        await delay(MOCK_LATENCY_MS)
        writeStoreSync(setDestinationConfig(readStoreSync(), ref, values))
    }
}

// Audience-insights backend calls. The fetch is injected (an authed fetch from
// useAuthedFetch), keeping this module framework-agnostic. Posts the audience
// config to its per-type insights endpoint and returns the LLM-built "what this
// audience buys" bar charts.
type AuthedFetch = (
    input: RequestInfo | URL,
    init?: RequestInit
) => Promise<Response>

// Each audience type has its own LLM endpoint (the search backends differ); they
// share the cache, the refresh endpoint, and the component. The request body for
// each *is* that type's config, so we post the config straight through.
const INSIGHTS_ENDPOINTS: Record<DatasetType, string> = {
    [DatasetType.TRANSACTION]: '/audience/txn_audience_insights',
    [DatasetType.USER_ATTRIBUTE]: '/audience/user_attribute_audience_insights',
    [DatasetType.CROSSED]: '/audience/crossed_audience_insights',
}

export async function fetchAudienceInsights(
    apiUrl: string,
    fetchFn: AuthedFetch,
    config: AudienceConfiguration
): Promise<AudienceInsightChart[]> {
    const res = await fetchFn(
        `${apiUrl}${INSIGHTS_ENDPOINTS[config.dataset_type]}`,
        {
            method: 'POST',
            body: JSON.stringify(config),
        }
    )
    if (!res.ok) {
        throw new Error(`Failed to load audience insights (${res.status})`)
    }
    return res.json() as Promise<AudienceInsightChart[]>
}

// Cheap, LLM-free refresh: replay the cached charts' searches live against the
// DB. Sends just each chart's title + search_request (its provenance) and gets
// back the same charts with fresh numbers. Shared across all types — the backend
// dispatches each chart on the dataset behind its search_request.
export async function refreshAudienceInsights(
    apiUrl: string,
    fetchFn: AuthedFetch,
    charts: AudienceInsightChart[]
): Promise<AudienceInsightChart[]> {
    const res = await fetchFn(`${apiUrl}/audience/insights/refresh`, {
        method: 'POST',
        body: JSON.stringify({
            charts: charts.map((c) => ({
                title: c.title,
                search_request: c.search_request,
            })),
        }),
    })
    if (!res.ok) {
        throw new Error(`Failed to refresh audience insights (${res.status})`)
    }
    return res.json() as Promise<AudienceInsightChart[]>
}
